/**
 * Cloudinary image transformation utility
 * Transforms product images to match Easyplant style:
 * - Square aspect ratio (1:1)
 * - Bottom-aligned (pot at bottom)
 * - White/clean background padding
 */

const CLOUDINARY_CLOUD_NAME = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME || 'demo';

export interface CloudinaryOptions {
  width?: number;
  height?: number;
  quality?: number;
  format?: 'auto' | 'webp' | 'jpg' | 'png';
}

/**
 * Transforms a product image URL using Cloudinary fetch API
 * Creates a square frame with the product aligned at the bottom
 */
export const transformProductImage = (
  originalUrl: string,
  options: CloudinaryOptions = {}
): string => {
  // Skip transformation for invalid or special URLs
  if (!originalUrl || 
      originalUrl.includes('res.cloudinary.com') ||
      originalUrl.startsWith('blob:') ||
      originalUrl.startsWith('data:') ||
      originalUrl === '/placeholder.svg' ||
      !originalUrl.startsWith('https://')) {
    return originalUrl;
  }

  const {
    width = 1200,
    height = 1200,
    quality = 90,
    format = 'auto'
  } = options;

  // Cloudinary transformations for Easyplant style:
  // f_auto - automatic format
  // q_auto - automatic quality
  // ar_1:1 - square aspect ratio (1:1)
  // c_fill - fill mode (extends background)
  // g_south - gravity south (align pot to bottom)
  // h_1200,w_1200 - dimensions for square frame (Easyplant standard)
  const transformations = [
    'f_auto',
    'q_auto',
    'ar_1:1',
    'c_fill',
    'g_south',
    `h_${height}`,
    `w_${width}`
  ].join(',');

  // Cloudinary fetch URL format
  const cloudinaryUrl = `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/image/fetch/${transformations}/${encodeURIComponent(originalUrl)}`;

  return cloudinaryUrl;
};

/**
 * Transform image for thumbnail/card view (smaller size)
 */
export const transformThumbnail = (originalUrl: string): string => {
  return transformProductImage(originalUrl, {
    width: 800,
    height: 800
  });
};

/**
 * Transform image for detail view (larger size)
 */
export const transformDetailImage = (originalUrl: string): string => {
  return transformProductImage(originalUrl, {
    width: 1200,
    height: 1200
  });
};
